import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Palette, Phone, Megaphone, Bot, MessageCircle, Lock, Search,
  Sparkles, Save, RotateCcw, Mail, ExternalLink, type LucideIcon,
} from "lucide-react";

import { Link } from "@tanstack/react-router";
import { MediaPickerDialog } from "@/components/admin/media-picker";
import { ImageIcon, Upload, X } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  validateSearch: (s: Record<string, unknown>) => ({
    section: typeof s.section === "string" ? s.section : undefined,
  }),
  component: SettingsAdmin,
});

type Setting = { key: string; value: any };

function useSettings() {
  return useQuery({
    queryKey: ["site_settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_settings").select("*");
      if (error) throw error;
      const map: Record<string, any> = {};
      for (const row of data as Setting[]) map[row.key] = row.value;
      return map;
    },
  });
}

type SectionDef = {
  id: string;            // url slug
  title: string;
  description: string;
  icon: LucideIcon;
  storeKey: string;      // site_settings.key
  defaults?: any;
  render: (ctx: SectionCtx) => ReactNode;
  // for search indexing
  searchTerms: string[];
};

type SectionCtx = {
  state: any;
  set: (v: any) => void;
};

const SECTIONS: SectionDef[] = [
  {
    id: "brand",
    title: "Brand",
    description: "Your public-facing name, tagline, and logo.",
    icon: Sparkles,
    storeKey: "brand",
    searchTerms: ["brand", "name", "tagline", "logo"],
    render: ({ state, set }) => (
      <>
        <Field label="Brand name" value={state.name} onChange={(v) => set({ ...state, name: v })} />
        <Field label="Tagline" value={state.tagline} onChange={(v) => set({ ...state, tagline: v })} />
        <LogoUploadField value={state.logo_url} onChange={(v) => set({ ...state, logo_url: v })} />
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact & social",
    description: "Public contact details surfaced across the site.",
    icon: Phone,
    storeKey: "contact",
    searchTerms: ["email", "phone", "whatsapp", "telegram", "messenger", "social"],
    render: ({ state, set }) => (
      <>
        <Field label="Email" value={state.email} onChange={(v) => set({ ...state, email: v })} />
        <Field label="Phone" value={state.phone} onChange={(v) => set({ ...state, phone: v })} />
        <Field label="WhatsApp number (intl format, no +)" value={state.whatsapp} onChange={(v) => set({ ...state, whatsapp: v })} />
        <Field label="Telegram username (without @)" value={state.telegram} onChange={(v) => set({ ...state, telegram: v })} />
        <Field label="Messenger username" value={state.messenger} onChange={(v) => set({ ...state, messenger: v })} />
      </>
    ),
  },
  {
    id: "hero",
    title: "Homepage hero",
    description: "Headline shown on the homepage hero block.",
    icon: Megaphone,
    storeKey: "hero",
    searchTerms: ["hero", "homepage", "headline", "cta"],
    render: ({ state, set }) => (
      <>
        <Field label="Heading" value={state.heading} onChange={(v) => set({ ...state, heading: v })} />
        <Field label="Subheading" value={state.subheading} onChange={(v) => set({ ...state, subheading: v })} multiline />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="CTA label" value={state.cta_label} onChange={(v) => set({ ...state, cta_label: v })} />
          <Field label="CTA URL" value={state.cta_url} onChange={(v) => set({ ...state, cta_url: v })} />
        </div>
      </>
    ),
  },
  {
    id: "theme",
    title: "Theme",
    description: "Color tokens applied across the site.",
    icon: Palette,
    storeKey: "theme",
    searchTerms: ["theme", "color", "primary"],
    render: ({ state, set }) => (
      <div className="flex items-end gap-3">
        <Field label="Primary color (hex)" value={state.primary} onChange={(v) => set({ ...state, primary: v })} />
        <div
          className="h-10 w-10 shrink-0 rounded-md border border-border"
          style={{ backgroundColor: state.primary || "transparent" }}
        />
      </div>
    ),
  },
  // Bot protection is managed in Admin → Integrations → Bot protection (single source of truth).

  {
    id: "ai-chat",
    title: "AI assistant",
    description: "Floating chat widget powered by an LLM.",
    icon: Bot,
    storeKey: "integrations",
    searchTerms: ["ai", "chat", "assistant", "gemini", "openai", "llm", "bot"],
    render: ({ state, set }) => (
      <>
        <ToggleRow
          label="Enable chat widget (floating bubble)"
          checked={!!state.chat_enabled}
          onChange={(v) => set({ ...state, chat_enabled: v })}
        />
        <div className="grid gap-2">
          <Label>AI provider</Label>
          <Select
            value={state.chat_provider ?? "gemini"}
            onValueChange={(v) => set({ ...state, chat_provider: v, chat_model: "" })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="openai">OpenAI (OPENAI_API_KEY)</SelectItem>
              <SelectItem value="gemini">Google Gemini (GEMINI_API_KEY)</SelectItem>
              <SelectItem value="anthropic">Anthropic Claude (ANTHROPIC_API_KEY)</SelectItem>
              <SelectItem value="groq">Groq (GROQ_API_KEY)</SelectItem>
              <SelectItem value="mistral">Mistral (MISTRAL_API_KEY)</SelectItem>
              <SelectItem value="deepseek">DeepSeek (DEEPSEEK_API_KEY)</SelectItem>
              <SelectItem value="openrouter">OpenRouter (OPENROUTER_API_KEY)</SelectItem>
              <SelectItem value="perplexity">Perplexity (PERPLEXITY_API_KEY)</SelectItem>
              <SelectItem value="xai">xAI Grok (XAI_API_KEY)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label="AI model" value={state.chat_model} onChange={(v) => set({ ...state, chat_model: v })} />
        <p className="-mt-2 text-xs text-muted-foreground">
          Examples: <code>gemini-3-flash-preview</code>, <code>gpt-4o-mini</code>,{" "}
          <code>claude-3-5-sonnet-latest</code>, <code>llama-3.3-70b-versatile</code> (Groq).
        </p>
        <Field label="Welcome message" value={state.chat_welcome} onChange={(v) => set({ ...state, chat_welcome: v })} />
        <div className="grid gap-2">
          <Label>System prompt</Label>
          <Textarea
            rows={4}
            value={state.chat_system_prompt ?? ""}
            onChange={(e) => set({ ...state, chat_system_prompt: e.target.value })}
            placeholder="You are a helpful support assistant for [Brand]…"
          />
        </div>
        <TestConnectionButton provider={state.chat_provider ?? "gemini"} model={state.chat_model ?? ""} />
      </>
    ),
  },
  {
    id: "channels",
    title: "External channels",
    description: "Floating contact buttons (WhatsApp, Telegram, Messenger).",
    icon: MessageCircle,
    storeKey: "integrations",
    searchTerms: ["whatsapp", "telegram", "messenger", "channels", "contact buttons"],
    render: ({ state, set }) => (
      <>
        <ToggleRow
          label="WhatsApp"
          checked={!!state.whatsapp_enabled}
          onChange={(v) => set({ ...state, whatsapp_enabled: v })}
        />
        <Field
          label="WhatsApp number (international format)"
          value={state.whatsapp_number}
          onChange={(v) => set({ ...state, whatsapp_number: v })}
        />
        <ToggleRow
          label="Telegram"
          checked={!!state.telegram_enabled}
          onChange={(v) => set({ ...state, telegram_enabled: v })}
        />
        <Field
          label="Telegram handle (without @)"
          value={state.telegram_handle}
          onChange={(v) => set({ ...state, telegram_handle: v })}
        />
        <ToggleRow
          label="Facebook Messenger"
          checked={!!state.messenger_enabled}
          onChange={(v) => set({ ...state, messenger_enabled: v })}
        />
        <Field
          label="Messenger URL (e.g. https://m.me/yourpage)"
          value={state.messenger_url}
          onChange={(v) => set({ ...state, messenger_url: v })}
        />
      </>
    ),
  },
  {
    id: "email",
    title: "Email",
    description: "Sender identity and default addresses for outgoing email.",
    icon: Mail,
    storeKey: "email",
    defaults: { from_name: "", from_email: "", reply_to: "", support_email: "", footer_address: "" },
    searchTerms: ["email", "smtp", "sender", "from", "reply-to", "mail", "notifications"],
    render: ({ state, set }) => (
      <>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="From name" value={state.from_name} onChange={(v) => set({ ...state, from_name: v })} />
          <Field label="From email" value={state.from_email} onChange={(v) => set({ ...state, from_email: v })} />
          <Field label="Reply-to address" value={state.reply_to} onChange={(v) => set({ ...state, reply_to: v })} />
          <Field label="Support email" value={state.support_email} onChange={(v) => set({ ...state, support_email: v })} />
        </div>
        <Field
          label="Footer mailing address"
          value={state.footer_address}
          onChange={(v) => set({ ...state, footer_address: v })}
          multiline
        />
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <Mail className="h-3.5 w-3.5" />
          <span>Manage templates, campaigns, providers and delivery logs in</span>
          <Button asChild variant="link" size="sm" className="h-auto px-1 text-xs">
            <Link to="/admin/email">
              Email &amp; notifications <ExternalLink className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </div>
      </>
    ),
  },
  {
    id: "auth",
    title: "Authentication",
    description: "Signup confirmation and login behavior.",
    icon: Lock,
    storeKey: "auth",
    defaults: { require_email_confirmation: true },
    searchTerms: ["auth", "login", "signup", "otp", "email confirmation", "password"],
    render: ({ state, set }) => (
      <>
        <ToggleRow
          label="Require email OTP confirmation on signup"
          checked={state.require_email_confirmation !== false}
          onChange={(v) => set({ ...state, require_email_confirmation: v })}
        />
        <p className="text-xs text-muted-foreground">
          When enabled, new users must enter a 6-digit code emailed to them before signing in.
          When disabled, accounts are usable immediately after signup (auto-confirm must also be enabled in the backend).
        </p>
      </>
    ),
  },


];

function SettingsAdmin() {
  const { data: settings, isLoading } = useSettings();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useRouterState({ select: (s) => s.location.search }) as { section?: string };

  const [query, setQuery] = useState("");

  const visibleSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.filter((s) =>
      [s.title, s.description, ...s.searchTerms].some((t) => t.toLowerCase().includes(q))
    );
  }, [query]);

  const activeId = search.section ?? SECTIONS[0].id;
  const activeSection =
    visibleSections.find((s) => s.id === activeId) ??
    SECTIONS.find((s) => s.id === activeId) ??
    SECTIONS[0];

  const setActive = (id: string) =>
    navigate({ to: "/admin/settings", search: { section: id } });

  const save = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { error } = await supabase.from("site_settings").upsert({ key, value });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site_settings"] });
      toast.success("Settings saved");
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  if (isLoading || !settings) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Site settings"
        description="Manage brand, contact, theme, integrations and authentication."
      />

      {/* WHMCS-style underline tab bar — one tab per section */}
      <div className="flex flex-wrap items-end justify-between gap-2 border-b border-border">
        <div className="flex flex-wrap items-center gap-0">
          {visibleSections.map((s) => {
            const Icon = s.icon;
            const isActive = s.id === activeSection.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                className={cn(
                  "relative -mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className={cn("h-4 w-4", isActive && "text-primary")} />
                <span>{s.title}</span>
              </button>
            );
          })}
        </div>

        <div className="relative ml-auto mb-2 w-full sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search settings…"
            className="h-8 pl-7 text-xs"
          />
        </div>
      </div>

      <main className="min-w-0">
        <SectionEditor
          key={activeSection.id}
          section={activeSection}
          initial={settings[activeSection.storeKey] ?? activeSection.defaults ?? {}}
          saving={save.isPending}
          onSave={(value) => save.mutate({ key: activeSection.storeKey, value })}
        />
      </main>
    </div>
  );
}

function SectionEditor({
  section, initial, saving, onSave,
}: {
  section: SectionDef;
  initial: any;
  saving: boolean;
  onSave: (v: any) => void;
}) {
  const [state, setState] = useState<any>(initial);
  useEffect(() => { setState(initial); }, [initial]);

  // For sections sharing a store key (integrations split into Security / AI / Channels),
  // we must preserve sibling fields when saving — merge state on top of the latest initial.
  const dirty = useMemo(
    () => JSON.stringify(state ?? {}) !== JSON.stringify(initial ?? {}),
    [state, initial]
  );

  const handleSave = () => onSave({ ...(initial ?? {}), ...(state ?? {}) });
  const handleReset = () => setState(initial);

  const Icon = section.icon;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex items-start gap-3">
          <div className="rounded-md border border-border bg-muted/50 p-2">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {section.title}
              {dirty && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  Unsaved
                </span>
              )}
            </CardTitle>
            <CardDescription>{section.description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {section.render({ state, set: setState })}
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={!dirty || saving}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
            <Save className="mr-1.5 h-3.5 w-3.5" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  label, value, onChange, multiline,
}: {
  label: string; value: string | undefined; onChange: (v: string) => void; multiline?: boolean;
}) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      {multiline ? (
        <Textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function LogoUploadField({
  value, onChange,
}: { value: string | undefined; onChange: (v: string) => void }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="grid gap-2">
      <Label>Logo</Label>
      <div className="flex items-start gap-3">
        <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-md border border-dashed border-border bg-muted/40">
          {value ? (
            <img src={value} alt="Brand logo" className="h-full w-full object-contain" />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <Input
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…/logo.png or upload below"
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setPickerOpen(true)}>
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              {value ? "Change logo" : "Upload logo"}
            </Button>
            {value && (
              <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")}>
                <X className="mr-1.5 h-3.5 w-3.5" />
                Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            PNG, JPG or SVG. Transparent background recommended.
          </p>
        </div>
      </div>
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        accept="image/*"
        onSelect={(_file, url) => { onChange(url); setPickerOpen(false); }}
      />
    </div>
  );
}

function ToggleRow({
  label, checked, onChange,
}: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2">
      <Label className="cursor-pointer">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function TestConnectionButton({ provider, model }: { provider: string; model: string }) {
  const [loading, setLoading] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={loading || !model}
      onClick={async () => {
        setLoading(true);
        try {
          const { testAiProvider } = await import("@/lib/chat.functions");
          const res = await testAiProvider({ data: { provider: provider as any, model } });
          if (res.ok) toast.success(`Connection OK: ${res.reply || "ok"}`);
          else toast.error(res.error || "Test failed");
        } catch (e: any) {
          toast.error(e?.message ?? "Test failed");
        } finally {
          setLoading(false);
        }
      }}
    >
      {loading ? "Testing…" : "Test connection"}
    </Button>
  );
}
